<script lang="ts">
  import { PUBLIC_S3_STATIC_FILES_URL } from '$env/static/public';

  import { createEventDispatcher, onMount } from 'svelte';

  import { fade } from 'svelte/transition';

  import { ShopApiDto } from '@do/data-transfer-objects';
  import { Status } from '@do/models';
  import { DirectusImage, Preset } from '@do/svelte-directus-images';

  import { clickOutside, formatExifDate } from '@shared/libs/tools';
  import type { DataItem, OriginalSizeSingle } from '@shared/types';
  import { AvatarPlaceholder } from '@shared/ui/avatar-placeholder';
  import { buildDataItemsParam, buildOriginalSizeParam } from '@shared/ui/tabs';
  import { cn } from '@shared/utils';

  import BtnCollapse from './btn-collapse.svelte';
  import BtnDetails from './btn-details.svelte';

  export let digitalOriginalData: ShopApiDto.ReadOneArtAssetResponse['digital_original'];
  export let artists: ShopApiDto.ReadOneArtAssetResponse['artists'];
  export let description: string;
  export let year: string;
  export let originalSizeX: OriginalSizeSingle;
  export let originalSizeY: OriginalSizeSingle;
  export let medium: DataItem[] | null = null;
  export let originalUnits: string | null;

  export let isOpen = false;

  const dispatch = createEventDispatcher<{
    moreDetails: void;
  }>();

  let isContentVisible = false;
  let detailsContainer: HTMLDivElement;
  let timer: ReturnType<typeof setTimeout>;
  let initialDetailsContainerWidth = 0; // we will store here label width in collapsed state
  let openDetailsContainerWidth = 350; // set block width in open state
  let isMounted = false;

  $: dateCreated = formatExifDate(digitalOriginalData?.exif?.CreateDate as string | null | undefined);

  $: photographer = digitalOriginalData?.photographer
    ? `${digitalOriginalData.photographer.first_name} ${digitalOriginalData.photographer.last_name}`
    : 'Digital Original Studio';

  const transitionDuration = 250;
  const transitionDelay = 300;

  function handleClickOutside() {
    if (isOpen) toggleOpen();
  }

  // different handlers for cases: We open block when clicking anywhere, but close block only if closebtn (or clickoutside) clicked
  function handleClickBlock(e: MouseEvent) {
    if (!isOpen) {
      e.preventDefault();
      toggleOpen();
    }
  }
  function handleClickBtn(e: MouseEvent) {
    e.stopPropagation();
    toggleOpen();
  }
  function toggleOpen() {
    clearTimeout(timer);
    if (isOpen) {
      isContentVisible = false;
      timer = setTimeout(() => {
        isOpen = false;
      }, transitionDuration);
    } else {
      isOpen = true;
      timer = setTimeout(() => {
        isContentVisible = true;
      }, transitionDuration);
    }
  }
  function onOpenChange(isOpen: boolean) {
    if (detailsContainer) {
      detailsContainer.style.width = `${isOpen ? openDetailsContainerWidth : initialDetailsContainerWidth}` + 'px';
    }
  }

  onMount(() => {
    initialDetailsContainerWidth = detailsContainer.clientWidth + 1; // add additional px in case if width is not integer
    detailsContainer.style.width = initialDetailsContainerWidth + 'px'; // define width property to make it possible to animate
    isMounted = true;
  });

  $: onOpenChange(isOpen);
</script>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  class="{cn(
    'font-neue bg-default-70/70 dark:bg-dark-500/60 dark:text-dark-900 text-default-900 max-w-[calc(100vw-40px)] cursor-auto rounded-2xl drop-shadow backdrop-blur-xl transition-[background-color,border-radius,width] duration-300 ease-in-out',
    {
      'cursor-pointer': !isOpen,
      'rounded-3xl': !isOpen,
      'bg-default-70/85': isOpen,
    },
  )}"
  on:clickOutsideAction="{handleClickOutside}"
  use:clickOutside="{!isOpen}"
  on:click="{handleClickBlock}"
  bind:this="{detailsContainer}"
>
  <div class="{cn('px-3 py-1.5 transition-[padding]', { 'py-3': isOpen })}">
    <!-- minimized variant -->
    <div class="flex items-center justify-between gap-3">
      <div
        class="{cn('flex items-center gap-2 truncate', {
          'pointer-events-none': !isOpen, // do not show link on hover when block is collapsed cuz we will prevent click by block opening
        })}"
      >
        <!-- artists photos block -->
        <div class="flex pl-2">
          {#each artists as artist, index}
            {@const isArtistPublished = artist.status === Status.Published}
            {@const href = isArtistPublished ? `/artist-${artist.id}-${artist.slug}` : undefined}
            <svelte:element
              this="{isArtistPublished ? 'a' : 'div'}"
              {href}
              class="{cn('-ml-2', { hidden: isOpen && index !== 0 })}"
              in:fade="{{ duration: transitionDuration, delay: transitionDelay * 2 }}"
              out:fade="{{ duration: transitionDuration, delay: 0 }}"
            >
              <div class="block h-6 w-6 shrink-0 overflow-hidden rounded-full">
                <DirectusImage
                  imageData="{artist.photo}"
                  alt="{artist.name}"
                  pictureSizes="{{
                    default: '32px',
                  }}"
                  presetKeys="{Preset.Mini}"
                  loading="lazy"
                  imgClasses="w-full h-full object-cover"
                >
                  <AvatarPlaceholder slot="default-placeholder" rounded name="{artist.name}" />
                </DirectusImage>
              </div>
            </svelte:element>
          {/each}
        </div>

        <span class="font-neue inline truncate text-sm font-semibold">
          <!-- artists list inline - multiple when close, single when open -->
          {#each artists as artist, index}
            {#if !isOpen || index === 0}
              <svelte:element
                this="{artist.status === Status.Published ? 'a' : 'span'}"
                href="/artist-{artist.id}-{artist.slug}/"
                class="inline"
                in:fade="{{ duration: transitionDuration, delay: 0 }}"
                out:fade="{{ duration: transitionDuration, delay: 0 }}"
                >{artist.name}{#if !isOpen && index < artists.length - 1},&nbsp{/if}</svelte:element
              >
            {/if}
          {/each}
          {#if !isOpen}
            <!-- artwork name in closed state -->
            <span
              class="inline font-medium"
              in:fade="{{ duration: transitionDuration, delay: transitionDelay * 2 }}"
              out:fade="{{ duration: transitionDuration, delay: 0 }}">– {description}</span
            >
          {/if}
        </span>
      </div>
      <BtnCollapse bind:isOpen on:click="{handleClickBtn}" />
    </div>

    <!-- collapsable part start -->
    <div
      class="{cn(
        'grid-rows-hidden grid min-w-0 overflow-hidden opacity-0 transition-[grid-template-rows,min-width,opacity] duration-300 ease-in-out',
        { 'grid-rows-1': isOpen },
        { 'opacity-1': isContentVisible },
      )}"
    >
      {#if isOpen || isMounted}
        <!-- do not show this block until we set width property to label cuz it can occur on block width -->
        <div
          class="mt-2 divide-y divide-black/10"
          in:fade="{{ duration: transitionDuration, delay: 0 }}"
          out:fade="{{ duration: transitionDuration, delay: transitionDelay * 2 }}"
        >
          <div class="pb-2">
            {#each artists as artist, index}
              {#if index !== 0}
                <svelte:element
                  this="{artist.status === Status.Published ? 'a' : 'div'}"
                  href="/artist-{artist.id}-{artist.slug}/"
                  class="mb-2 flex items-center gap-2"
                >
                  <div class="block h-6 w-6 shrink-0 overflow-hidden rounded-full">
                    <DirectusImage
                      imageData="{artist.photo}"
                      alt="{artist.name}"
                      pictureSizes="{{
                        default: '32px',
                      }}"
                      presetKeys="{Preset.Mini}"
                      loading="lazy"
                      imgClasses="w-full h-full object-cover"
                    >
                      <AvatarPlaceholder slot="default-placeholder" rounded name="{artist.name}" />
                    </DirectusImage>
                  </div>

                  <span class="font-neue text-sm font-semibold">{artist.name}</span>
                  <!-- {#if artist.verification_status === ArtistVerificationStatus.Verified}
                <Icon name="verified" />
              {/if} -->
                </svelte:element>
              {/if}
            {/each}
            <p class="mb-1 text-sm font-medium">{description}</p>
            <p class="dark:text-dark-900/80 mb-1 text-sm">
              {#if year}
                {year},
              {/if}

              {#if originalSizeX && originalSizeY}
                {buildOriginalSizeParam([originalSizeX, originalSizeY], originalUnits)}
              {/if}

              {#if medium?.length}
                {buildDataItemsParam(medium)}
              {/if}
            </p>
          </div>

          <div class="pt-3">
            <div class="flex flex-col gap-1.5 text-sm">
              <div class="flex items-center justify-between">
                <span class="flex-0.4 dark:text-dark-900/80"> Resolution: </span>

                <div class="flex-0.7">
                  {#if digitalOriginalData?.mpx}
                    <span class="rounded-md font-bold">{digitalOriginalData.mpx} MPX</span>
                  {/if}

                  {#if digitalOriginalData?.exif?.ImageWidth && digitalOriginalData?.exif?.ImageHeight}
                    ({digitalOriginalData.exif.ImageWidth} x {digitalOriginalData.exif.ImageHeight} px)
                  {/if}
                </div>
              </div>

              <div class="flex items-center justify-between">
                <span class="flex-0.4 dark:text-dark-900/80">Color depth:</span>

                <p class="flex-0.7">
                  <b>48 bit</b>

                  <span>
                    <!-- <Icon
                    name="colors"
                    variant="{is($theme === 'dark', 'dark', 'default')}"
                    classes="mr-1 inline-block align-top"
                  /> -->
                    <img
                      src="{PUBLIC_S3_STATIC_FILES_URL}rainbow-icon_3x.png"
                      class="-mt-0.5 ml-1 mr-1 inline-block h-[18px] w-[18px] align-text-top"
                      alt="Rainbow icon"
                      loading="lazy"
                    />
                    <b>281 Trillion Colors</b>
                  </span>
                </p>
              </div>

              {#if digitalOriginalData?.exif?.FileSize}
                <div class="flex items-center justify-between">
                  <p class="flex-0.4 dark:text-dark-900/80">Original file size:</p>

                  <b class="flex-0.7">{digitalOriginalData.exif.FileSize} DNG File</b>
                </div>
              {/if}

              <div class="flex items-center justify-between">
                <p class="flex-0.4 dark:text-dark-900/80">Photographer:</p>

                <b class="flex-0.7">{photographer}</b>
              </div>

              {#if dateCreated}
                <div class="flex items-center justify-between">
                  <p class="flex-0.4 dark:text-dark-900/80">Captured on:</p>

                  <b class="flex-0.7">{dateCreated}</b>
                </div>
              {/if}
            </div>
            <div class="mt-4 flex gap-3">
              <BtnDetails on:click="{() => dispatch('moreDetails')}">More details</BtnDetails>
              {#if artists.length === 1}
                {@const isArtistPublished = artists[0].status === Status.Published}
                {@const href = isArtistPublished ? `/artist-${artists[0].id}-${artists[0].slug}` : undefined}
                {#if href}
                  <BtnDetails {href} target="_blank">Artist info</BtnDetails>
                {/if}
              {/if}
            </div>
          </div>
        </div>
      {/if}
    </div>
    <!-- collapsable part end -->
  </div>
</div>
